import React, { useState } from 'react'
import { useNavigate, generatePath } from 'react-router-dom'
import { TABLE } from 'routes'
import { ITableView } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import ActionDialog from 'components/dialog/action'
import { copyAsCustomView } from 'components/spreadsheet/contexts/data/actions'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { openCustomViewPanel } from 'components/spreadsheet/components/custom-view/panel'
import { ViewTypes } from 'components/spreadsheet/constants/const'

/**
 * Whether an owner can copy this view as a Custom View to change with MortaAI: a Form, not a personal view's, not
 * a synced table's (a Custom View's page can't add rows there), and never from inside a document.
 */
export const canEditWithAi = (
  view: Pick<ITableView, 'type' | 'isPersonalView'>,
  isAdmin: boolean,
  isSynced: boolean,
  inProcess: boolean
): boolean => isAdmin && !inProcess && view.type === ViewTypes.FORM && !view.isPersonalView && !isSynced

interface Asking {
  view: Pick<ITableView, 'publicId' | 'name'>
  nestedInMenu: boolean
  onDone?: () => void
}

/**
 * Edit with MortaAI, from the view's menu or the form's pen. It first says what it does (makes a Custom View that
 * starts as the same form, and leaves the form as it is) and asks; then copies the Form, opens the copy and opens
 * MortaAI's panel beside it. Render `dialog` wherever `ask` is called from.
 */
export const useEditWithAi = (): {
  copying: boolean
  ask: (view: Pick<ITableView, 'publicId' | 'name'>, options?: { nestedInMenu?: boolean; onDone?: () => void }) => void
  dialog: React.ReactNode
} => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const navigate = useNavigate()
  const [copying, setCopying] = useState(false)
  const [asking, setAsking] = useState<Asking | null>(null)

  // Resolves once the copy is open; rejects, having said why, if it couldn't be made, leaving the dialog open.
  const copy = (viewId: string): Promise<void> =>
    new Promise((resolve, reject) => {
      setCopying(true)
      copyAsCustomView(
        project.publicId,
        viewId,
        spreadsheetData,
        setSpreadsheetData,
        (newViewId) => {
          setCopying(false)
          navigate(
            generatePath(TABLE, {
              projectId: project.publicId,
              tableId: spreadsheetData.tableDetails.publicId,
              viewId: newViewId
            }),
            { replace: true }
          )
          openCustomViewPanel('ai')
          setSnackbarMessage({ status: 'success', message: 'Copied as a Custom View. Ask MortaAI to change it.' })
          resolve()
        },
        (error) => {
          setCopying(false)
          displayErrorMessage(error)
          reject(error)
        }
      )
    })

  const dialog = asking && (
    <ActionDialog
      title="Create a Custom View from this form?"
      description={`MortaAI works on a copy of this form, saved as a new Custom View named ‘${asking.view.name} (custom)’. The original form isn't changed.`}
      confirmLabel="Create Custom View"
      nestedInMenu={asking.nestedInMenu}
      onClose={() => setAsking(null)}
      onConfirm={() => copy(asking.view.publicId).then(() => asking.onDone?.())}
    />
  )

  return {
    copying,
    ask: (view, options = {}) => {
      if (!copying) setAsking({ view, nestedInMenu: !!options.nestedInMenu, onDone: options.onDone })
    },
    dialog
  }
}
